import sanitizeHtml from 'sanitize-html';
import { i18n } from '/plugins/i18n.client';
import moment from 'moment-timezone';
import numeral from 'numeral';
/**
 * General utility for widely used functions.
 *
 * @module common/general-utils
 */

// All time units in milliseconds.
const unitsWithMS = [
  { unit: 'y', value: 31557600000 },
  { unit: 'mo', value: 2629800000 },
  { unit: 'w', value: 604800000 },
  { unit: 'd', value: 86400000 },
  { unit: 'h', value: 3600000 },
  { unit: 'm', value: 60000 },
  { unit: 's', value: 1000 },
  { unit: 'ms', value: 1 }
];

const filterExpressions = {
  '=': 'equals',
  '<>': 'different',
  '<': 'lower than',
  '>': 'greater than',
  '<=': 'lower than/equals to',
  '>=': 'greater than/equals to',
  IN: 'in',
  'IS NULL': 'is empty',
  'IS NOT NULL': 'not empty'
};

sanitizeHtml.defaults.transformTags = {
  img: function (tagName, attribs) {
    attribs.src = attachQueryParam(attribs.src);
    return { tagName, attribs };
  }
};

export default sanitizeHtml;

/**
 * Function to convert millisecond into human readable time format
 * @param {number} millisecond millisecond to convert into human readable format
 * @return {String} result as human readable time
 */
export const timeHumanizer = (millisecond) => {
  let result = '';

  if (millisecond === 0) {
    return '0 ms';
  }

  for (const unitObj of unitsWithMS) {
    let value = millisecond / unitObj.value;
    if (value >= 1) {
      result = Math.floor(value) + ' ' + unitObj.unit;
      break;
    }
  }
  return result;
};

/**
 * Function to convert all the keys of object to lowercase
 * @param {Object} obj
 * @method
 */
export const keysToLowerCase = (obj) => {
  if (!obj) {
    return {};
  }
  const keys = Object.keys(obj);
  let index = keys.length;
  let lastKey;
  while (index > 0) {
    index--;
    const key = keys[index];
    lastKey = key.toLowerCase();
    if (key === lastKey) continue;

    obj[lastKey] = obj[key];
    delete obj[key];
  }
  return obj;
};

/**
 * This function takes a string which need to be encoded to Base64 and
 * returns the encoded string.
 * @param {String} field
 * @returns {String} Encoded field
 */
export const encodeSingleFieldToBase64 = (field) => {
  // if the field is already encoded, return as it is
  if (field && field.indexOf('base64,') !== 0) {
    return 'base64,esc,' + btoa(escape(field));
  }
  return field;
};

export const decodeSingleFieldFromBase64 = (field) => {
  if (!field) {
    return field;
  }
  if (field.indexOf('base64,esc,') === 0) {
    try {
      return unescape(atob(field.replace('base64,esc,', '')));
    } catch (e) {
      return field;
    }
  }
  return field;
};

export const encodeFieldsToBase64 = (object, fieldsToConvert) => {
  if (!object || !fieldsToConvert) {
    return object;
  }
  let encodedObject = fieldsToConvert
    .filter((prop) => object[prop])
    .reduce(
      (encodedObject, prop) => {
        let propValue = object[prop];
        if (typeof propValue === 'object') {
          propValue = JSON.stringify(propValue);
          encodedObject.stringifiedFields.push(prop);
        }
        encodedObject[prop] = encodeSingleFieldToBase64(propValue);
        return encodedObject;
      },
      { stringifiedFields: [] }
    );
  encodedObject.stringifiedFields = encodeSingleFieldToBase64(JSON.stringify(encodedObject.stringifiedFields));
  return Object.assign({}, object, encodedObject);
};
/**
 * Iterate recursively through the job details and convert any n-level nesting
 * to 1 level nest
 * i.e. {a:123,b:{c:123,d:[12,33]}, f:{d:{'w'}}} becomes
 * [{key:'a',value:123},
 * {key:'b[c]',value:123},
 * {key:'b[d][0]',value:12},
 * {key:'b[d][1]',value:33},
 * ... and so on.
 * ]
 * @param {*} finalObj object to de nestify
 * @param {*} rootKey parent key name if any , if not pass ''
 * @param {Array} [keysToIgnore=[]]
 */
export const deNestifyObject = (finalObj, rootKey, keysToIgnore = []) => {
  let formData = [];
  Object.keys(finalObj).forEach((key) => {
    if (keysToIgnore.indexOf(key) === -1) {
      let newKey = rootKey ? rootKey + '[' + key + ']' : key;
      if (typeof finalObj[key] === 'object' && finalObj[key] !== null) {
        formData = formData.concat(deNestifyObject(finalObj[key], newKey, keysToIgnore));
      } else {
        formData = formData.concat({
          key: newKey,
          value: finalObj[key]
        });
      }
    }
  });
  return formData;
};

/**
 * Create formdata object using javascript's FormData API
 * Used for uploading .ppk files
 * @param {Object} finalObj Data to be uploaded
 * @param {Array} files files to be uploaded
 */
export const createFormUploadObject = (finalObj, files) => {
  let formData = new FormData();

  let simplifiedFormData = deNestifyObject(finalObj, '', ['formData']);
  simplifiedFormData.forEach((row) => {
    formData.append(row.key, row.value);
  });
  if (files && files.length > 0) {
    files.forEach((file, ind) => {
      formData.append('file[' + ind + ']', file);
    });
  }

  return formData;
};

/**
 * Calculate chunks quantity and then add into `chunksQueue`
 * @param {Object} file Data to be uploaded
 * @param {Bytes} chunkSize Size of chunks
 * @param {Callback} callback Callback function to upload file in server
 */
export const uploadFile = async (file, chunkSize, callback) => {
  if (!file) return null;
  const chunkQuantity = Math.ceil(file.size / chunkSize);
  const chunksQueue = [...Array(chunkQuantity).keys()].reverse(); //reversed it because  want to send parts consistently and take next part's id from the end.
  return await sendNext(chunksQueue, chunkSize, file, {
    callback: callback
  });
};

/**
 * Slice file according to chunkSize and upload chunk to server
 * @param {Array} chunksQueue Array of chunkId
 * @param {Bytes} chunkSize Size of chunk
 * @param {Object} file File object
 * @param {Callback} callback Callback function to upload file in server
 */
async function sendNext(chunksQueue, chunkSize, file, { callback, result }) {
  if (!chunksQueue.length) {
    await callback();
    return Promise.resolve(result);
  }

  const chunkId = chunksQueue.pop();
  const begin = chunkId * chunkSize;
  const chunk = file.slice(begin, begin + chunkSize); //Divide file according to chunk size.
  let formdata = new FormData();
  formdata.append('_currentChunkSize', chunk.size);
  formdata.append('_chunkSize', chunkSize);
  formdata.append('_chunkNumber', chunkId);

  formdata.append('file', chunk, file.name);
  formdata.append('_totalSize', file.size);
  result = await callback(formdata, !chunksQueue.length, chunkId); //Upload chunk to server
  return await sendNext(chunksQueue, chunkSize, file, {
    callback: callback,
    result: result
  });
}

/**
 * Checks translation regex validity
 * @param {String} textContent string to check in regex expression
 */
export const checkTranslationRegex = (textContent) => /\$\$[\s\S]+?\$\$/g.test(textContent);

export const checkDynamicValue = (textContent) => {
  const regex = /\[\[([^[\]]+|[^[\]]+\[[^[\]]+\])\]\]/g;
  const iterator = textContent.matchAll(regex);
  for (const item of iterator) {
    const match = item[0];
    if (match !== '[[website_host]]') {
      return true;
    }
  }
  return false;
};
/**
 * Open specified url into new tab of window inside pop up
 * @param {String} url
 * @param {String} title Title of page
 * @param {Number} w Width of the pop up
 * @param {Number} h Height of the pop up
 */
export const popupCenter = (url, title, w, h) => {
  let dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : screen.left;
  let dualScreenTop = window.screenTop !== undefined ? window.screenTop : screen.top;

  let clientWidth = document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
  let clientHeight = document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;
  let width = window.innerWidth ? window.innerWidth : clientWidth;
  let height = window.innerHeight ? window.innerHeight : clientHeight;

  let left = width / 2 - w / 2 + dualScreenLeft;
  let top = height / 2 - h / 2 + dualScreenTop;
  let newWindow = window.open(
    url,
    title,
    'scrollbars=yes, width=' + w + ', height=' + h + ', top=' + top + ', left=' + left
  );

  // Puts focus on the newWindow
  if (window.focus) {
    newWindow.focus();
  }
};

/**
 * Encodes Html by replacing some char
 * @param {String} html html string to be encoded
 */
export const encodeHtml = (html) => {
  if (!html || typeof html !== 'string') {
    return html;
  }
  let replaceChar = {
    '<': '&lt;',
    '>': '&gt;',
    '&': '&amp;'
  };
  return html.replace(/[<>&]/g, (match) => replaceChar[match]);
};

/**
 * Process response and show error message
 *
 * @param {String} action
 * @param {Object} error
 * @param {Object} defaultMessage
 */
export { errorFromResponse } from './general-utils-base';

/**
 * Convert flat array to n level nested
 * @param {Array} list
 * @param {String} nestId
 * @param {String} parentId
 * @param {Object} options extra options
 * @param {Boolean=} options.allowMissingParent if given, will not remove elements whose parent is not present
 * @param {String=} options.breadcrumbKeyName (Optional) if given, stores path to parent
 * using the value provided here
 * @returns {Array} nlevel nested array
 */
export const nLevelArrayNest = (list, nestId, parentId, { allowMissingParent, breadcrumbKeyName } = {}) => {
  // Create an entity map for easy indexing
  let e = {};
  const items = [...list].map(({ ...i }) => i);
  items.forEach((n) => (e[n[nestId]] = n));

  // Add children to parents that exist (some parents don't exist - a data error)
  let nestedList = items.map((n) => {
    /* Add child key if not already exists */
    if (!n.children) {
      n.children = [];
    }

    /* Form breadcrumb list if key provided */
    if (breadcrumbKeyName) {
      n.breadcrumb = [n[breadcrumbKeyName]];
    }
    if (!n[parentId]) {
      return n;
    }
    let parent = e[n[parentId]];
    if (!parent) {
      if (allowMissingParent) {
        return n;
      }
      // remove the non-existent parent property
      delete n[parentId];
      return null;
    }

    if (!parent.children) {
      parent.children = [];
    }

    if (breadcrumbKeyName) {
      parent.breadcrumb = parent.breadcrumb || [parent[breadcrumbKeyName]];
      n.breadcrumb = [...parent.breadcrumb, ...n.breadcrumb];
    }
    parent.children.push(n);

    // mark record as empty and to be removed
    return null;
  });

  // remove the empty records
  nestedList = nestedList.filter((n) => n);

  return nestedList;
};

/**
 * Attach required query param (from_markdown) to links before rendering html
 * @param {string} url image/link to be rendered in html
 */
export const attachQueryParam = (url = '') => {
  if (!url.includes('?')) {
    url = url.endsWith('/') ? url.slice(0, -1) : url;
    return url + '?from_markdown=true';
  }

  const queryParams = {};
  const queryParamsString = url.substring(url.indexOf('?') + 1);
  queryParamsString.split('&').forEach((queryParam) => {
    const [key, value] = queryParam.split('=');
    queryParams[key] = value;
  });

  if ('from_markdown' in queryParams) return url;

  return url + '&from_markdown=true';
};

/**
 * Replaces dynamic value stored as`[[column_name]]` format
 */
export const replaceAndTranslateDynamicValues = (
  dynamicString,
  item = {},
  options = {
    allowedTags: sanitizeHtml.defaults.allowedTags,
    allowedAttributes: sanitizeHtml.defaults.allowedAttributes,
    allowedSchemesByTag: sanitizeHtml.defaults.allowedSchemesByTag
  }
) => {
  if (typeof item === 'string') {
    try {
      item = JSON.parse(item);
    } catch (error) {
      item = {};
    }
  }
  dynamicString = setTranslatedValues(i18n.global.t(dynamicString || ''));
  if (!dynamicString.match(/\[\[([^\s[].*?)\]\]/g)) {
    return sanitizeHtml(dynamicString, options).replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  }
  let newItems = Object.keys(item || {}).reduce((object, key) => ({ ...object, [key.toLowerCase()]: item[key] }), {});
  let formattedItems = Object.keys(item?.formattedValues$$ || {}).reduce(
    (object, key) => ({ ...object, [key.toLowerCase()]: item.formattedValues$$[key] }),
    {}
  );
  return dynamicString.replace(/\[\[([^\s[].*?)\]\]/g, (_match, columnName) => {
    const matches = columnName.match(/([^EX:]=?)(.*)(?=\|)/gi);
    const column = matches && matches.length > 0 ? matches[0] : columnName;

    // Test if column name is a function (DateFormat, RoundDecimal) and get value based on function definition.
    if (/(\w+)(?=\()/g.test(column) && column.match(/DateFormat|RoundDecimal|EscapeQuotes/gi)) {
      return _getValueBasedOnFunction(column, newItems, options);
    }
    let dynamicValue =
      formattedItems[column.toLowerCase()] !== undefined
        ? formattedItems[column.toLowerCase()]
        : newItems[column.toLowerCase()];
    if (
      formattedItems[column.toLowerCase()] === undefined &&
      !Object.prototype.hasOwnProperty.call(newItems, column.toLowerCase())
    ) {
      return _match;
    }
    if (Array.isArray(dynamicValue)) {
      dynamicValue = dynamicValue.join(', ');
    } else if (dynamicValue && typeof dynamicValue === 'object') {
      const checkedValues = Object.keys(dynamicValue).filter((key) => dynamicValue[key] === true);
      dynamicValue = checkedValues.length ? checkedValues.join(', ') : JSON.stringify(dynamicValue);
    }
    if (dynamicValue === null || dynamicValue === undefined) {
      return null;
    }
    return sanitizeHtml(setTranslatedValues(i18n.global.t(dynamicValue.toString())), options)
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  });
};

// Get value based on function.
const _getValueBasedOnFunction = (fnDefinition, newItems, options) => {
  const fnName = /(\w+)(?=\()/g.exec(fnDefinition)[0].toUpperCase();
  const args = /\(\s*([^)]+?)\s*\)/.exec(fnDefinition)[1].split(',');
  let dynamicValue = args[0].trim();
  // When value is not static (columnName) fetch value from data.
  if (isNaN(dynamicValue) && !moment(dynamicValue, moment.ISO_8601).isValid()) {
    dynamicValue = newItems[args[0].trim().toLowerCase()];
  }
  if (dynamicValue) {
    switch (fnName) {
      // Convert date as per user's format.
      case 'DATEFORMAT':
        if (isNaN(dynamicValue) && moment(dynamicValue, moment.ISO_8601).isValid()) {
          dynamicValue = moment(dynamicValue).format();
        }
        break;
      // Rounding decimal values with given number. (i.e. => RoundDecimal(1.5567, 2) => 1.56)
      case 'ROUNDDECIMAL':
        dynamicValue = numeral(parseFloat(dynamicValue)).format('0,0.' + '0'.repeat(Number(args[1]) || 2));
        break;
      case 'ESCAPEQUOTES':
        dynamicValue = dynamicValue.replace(/'/g, "\\'").replace(/"/g, '&quot;');
        return dynamicValue;
    }
  }
  return dynamicValue ? sanitizeHtml(dynamicValue, options) : dynamicValue;
};

export const getDynamicColumnNames = (dynamicString, containFns = false) => {
  let columnNames = [];
  dynamicString.replace(/\[\[([^\s[].*?)\]\]/g, (_match, columnName) => columnNames.push(columnName));
  // When string can contain functions.
  if (containFns) {
    // Update columnNames accordingly (i.e. RoundDecimal(column, x) => column)
    columnNames = columnNames.reduce((acc, col) => {
      let value = col;
      // Fetch arguments of function.
      const match = /\(\s*([^)]+?)\s*\)/.exec(col);
      if (match && match[1] && col.match(/DateFormat|RoundDecimal|EscapeQuotes/gi)) {
        const args = match[1].split(',');
        const dynamicValue = args[0]?.trim();
        // When value is static (numeric or date) do not add column.
        if (!isNaN(dynamicValue) || moment(dynamicValue, moment.ISO_8601).isValid()) {
          return acc;
        }
        // Set first argument as value (RoundDecimal(column, x) => column)
        value = dynamicValue;
      }
      acc.push(value);
      return acc;
    }, []);
  }
  return columnNames;
};

/**
 * Replaces dynamic value stored as [[website_host]], [[columnName]], [[columnName[key]]] format
 */
export const replaceDynamicReferencesOutsideStrings = (script, replacement) => {
  const stringLiterals = [];
  const scriptWithoutStringLiterals = script.replace(/(['"`])(?:\\.|(?!\1)[\s\S])*\1/g, (match) => {
    const index = stringLiterals.push(match) - 1;
    return `__CUSTOM_VALIDATION_STRING_${index}__`;
  });
  const updatedScript =
    typeof replacement === 'function'
      ? replacement(scriptWithoutStringLiterals)
      : scriptWithoutStringLiterals.replace(/\[\[([^[\]]+)\]\]/g, replacement);

  return updatedScript.replace(/__CUSTOM_VALIDATION_STRING_(\d+)__/g, (_match, index) => stringLiterals[index]);
};

export const setDynamicValues = (dynamicString, item, options = {}) =>
  dynamicString &&
  dynamicString.replace(/\[\[([^[\]]+|[^[\]]+\[[^[\]]+\])\]\]/g, (_match, columnName) => {
    let dynamicValue;
    if (columnName.toLowerCase() === 'website_host') {
      dynamicValue = location.origin;
    } else if (columnName.match(/\[([^\]]+)]/)) {
      //for nested object keys [[columnName[key]]]
      if (item[columnName.split('[')[0]] && typeof item[columnName.split('[')[0]] === 'object')
        dynamicValue = item[columnName.split('[')[0]][columnName.match(/\[([^\]]+)]/)[1]];
    } else if (item[columnName] !== null && item[columnName] !== undefined) {
      // [[columnName]]
      const value = item[columnName];
      dynamicValue = typeof value === 'object' ? JSON.stringify(value) : value;
    }
    /**
     * Check value is date or not.
     * If date, Format it based on user's format.
     */
    if (dynamicValue && isNaN(dynamicValue) && moment(dynamicValue, moment.ISO_8601).isValid()) {
      dynamicValue = moment(dynamicValue).format();
    }
    if (!dynamicValue && options.forScriptExecution) {
      return 'null';
    }
    if (options.forScriptExecution) {
      return JSON.stringify(dynamicValue);
    }
    return dynamicValue !== null && dynamicValue !== undefined
      ? sanitizeHtml(dynamicValue, { allowedAttributes: false })
      : '';
  });

export const setSystemVariables = (dynamicString, getSystemVariable) =>
  dynamicString.replace(/\[\[([^[\]]+|[^[\]]+\[[^[\]]+\])\]\]/g, (_match, columnName) => {
    let dynamicValue;
    if (columnName.toLowerCase().startsWith('env_')) {
      dynamicValue = getSystemVariable(columnName.substring(4));
    }
    return dynamicValue !== null && dynamicValue !== undefined
      ? sanitizeHtml(dynamicValue, { allowedAttributes: false })
      : _match;
  });

/**
 * converts {a:{b: 'abc'}} to {'a.b' : 'abc }
 * @param {Object} data translation data
 */
export { flattenedTranslations } from './general-utils-base';

/**
 * Gets string value
 */
export const getStringValue = (value) => (isNaN(value) ? `` : value.toString());

export const setTranslatedValues = (dynamicString) =>
  dynamicString && dynamicString.replace(/\$\$([\s\S]+?)\$\$/g, (match) => i18n.global.t(match));

export const getDateFormatString = (user) => {
  const dateFormat = (user && user.dateFormat) || 'DD/MM/YYYY';
  const timeFormat = user && user.EnableMeridiemFormat ? 'hh:mm:ss A' : 'HH:mm:ss';

  return `${dateFormat} ${timeFormat}`;
};

/**
 * Replaces dynamic value stored as`{{column_name}}` format
 */
export const replaceDynamicColumnValues = (condition) =>
  condition.replace(/'\{\{([^\s[].*?)\}\}'/g, (_match, columnName) =>
    isNaN(columnName) && columnName !== 'null' ? `'${columnName}'` : columnName
  );

/**
 * Function to return formatted numeric values.
 * @param value contains value of field
 * @param {Object} objectFieldDetails contains additional data of that field
 */
export const numberFormat = (value, objectFieldDetails) => {
  const isFormula =
    objectFieldDetails.FieldType === 'FORMULA' || objectFieldDetails.ParentFieldSettings?.FieldType === 'FORMULA';

  if (isFormula) {
    if (value === null || value === undefined) {
      return value;
    }
    const format = parseDecimalFormat(objectFieldDetails);
    return format ? formatCalculatedValue(value, format) : value;
  }

  if (objectFieldDetails.Settings.IsMultiSelect) {
    let values = value?.split(',') || [];
    values.forEach((value, index, arr) => {
      arr[index] = numeral(parseFloat(value)).format(parseDecimalFormat(objectFieldDetails));
    });
    return values.join(', ');
  }
  return numeral(value).format(parseDecimalFormat(objectFieldDetails));
};

/**
 * Function to return formatted calculated/formula field numeric values.
 * Leverages Numeral.js for numeric/locale formatting while preserving custom Unicode currency
 * symbols (e.g. '€ 0,0', '€0,0', '0,0 €') without them being stripped by Numeral.js.
 * @param {Number|String} value contains value of field
 * @param {String} format contains format string
 */
export const formatCalculatedValue = (value, format) => {
  if (value === null || value === undefined || value === '' || !format || Number.isNaN(Number(value))) {
    return value;
  }
  const symbol = format.match(/\p{Sc}/u)?.[0];
  if (!symbol || symbol === '$') return numeral(value).format(format);

  const pattern = format.replace(/\p{Sc}/gu, '').trim() || '0,0';
  const formatted = numeral(value).format(pattern);

  return format
    .replace(pattern, formatted)
    .replace(/^([^0-9()+-]+)-/, '-$1')
    .replace(/^([^(]+)\((.*)\)$/, '($1$2)');
};

/**
 * Method to remove the nested square bracket sub string from main string
 * @param {String} value
 * @returns string without nested square bracket sub string
 */
export { removeNestedBrackets } from './general-utils-base';

/**
 * Evaluated the filter and converts to the InlineFiler format and replaces the dynamic content with its values
 * @param {Object} group Object containing rule group condition details
 * @param {Object} dynamicValues dynamic value for replacing dynamic contents
 * @returns Object containing filter rule group for InlineFilter
 */
export const evaluateFilter = (group, dynamicValues) => {
  // Use raw db values for filter evaluation, not formatted display values
  const { formattedValues$$: _formatted, ...rawValues } = dynamicValues || {};
  return {
    filter: group.rules.reduce((filter, rule) => {
      filter.push({
        field: rule.key,
        operator: filterExpressions[rule.operator],
        value: rule.value ? replaceAndTranslateDynamicValues(rule.value, rawValues) : ''
      });
      return filter;
    }, []),
    filterOperation: group.condition,
    groups:
      group.groups?.reduce((inlineGroups, group) => {
        inlineGroups.push(evaluateFilter(group, dynamicValues));
        return inlineGroups;
      }, []) || []
  };
};

const parseDecimalFormat = (fieldDetail) => {
  if (fieldDetail.FieldType === 'FORMULA') {
    return fieldDetail.Settings?.format;
  }
  if (fieldDetail.ParentFieldSettings?.FieldType === 'FORMULA') {
    return fieldDetail.ParentFieldSettings.Settings?.format;
  }
  const isDecimal =
    !!fieldDetail.Settings.enableDecimals ||
    fieldDetail.Settings.isDecimal === 1 ||
    fieldDetail.ParentFieldSettings?.Settings.isDecimal === 1;
  const decimalValue =
    fieldDetail.Settings.DecimalValue ||
    fieldDetail.ParentFieldSettings?.Settings.DecimalValue ||
    fieldDetail.Settings.decimalValue ||
    3;
  if (isDecimal) {
    return '10,000.' + '0'.repeat(decimalValue);
  }
  return undefined;
};

// render icon function
export const convertIcon = (icon) => {
  if (icon?.startsWith('fa-')) {
    return `fa ${icon}`;
  }
  return icon;
};

/**
 * Get date in ISO string format
 * @param {Date} date
 */
export const getISODateString = (date) => moment(date).format('YYYY-MM-DD');

/**
 * Get datetime in ISO string format
 * @param {Date} datetime
 */
export const getISODateTimeString = (datetime) => moment(datetime).format('YYYY-MM-DD HH:mm:ss');

/**
 * Function to generate dates between provided start and end date
 * @param {Date} startDate
 * @param {Date} endDate
 */
export const generateDatesInRange = (startDate, endDate) => {
  const dates = [];
  let currentDate = new Date(startDate);
  endDate = new Date(endDate);
  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
  }
  return dates;
};

export const nextTicks = async (wrapper, n) => {
  for (let i = 0; i < n; i++) {
    await wrapper.vm.$nextTick();
  }
};

export { generateRequestId } from './general-utils-base';
